import { useCallback, useEffect, useState } from "react";
import type { TonConnectUI, Wallet } from "@tonconnect/ui";
import { api, ApiError } from "./api.js";

export interface WalletView {
  wallet: { address: string; appName: string | null; withdrawAfter: number; connectedAt: number } | null;
  deposit: { vault: string | null; memo: string; min: string; left: string; paused: boolean };
  withdraw: { min: string; fee: string; available: string; open: boolean };
  activity: { kind: "deposit" | "withdrawal"; id: string; amount: string; state: string; at: number }[];
  /** Set only while the admin shows proof of funds and the vault covers every balance. Never carries amounts. */
  backed: { checkedAt: number } | null;
  serverTime: number;
  /** When this phone received it, to read the server's clock from the phone's. */
  fetchedAt: number;
}

/** The wallets the Connect screen offers by name, as the TON Connect wallet list calls them. */
export const WALLET_APPS = {
  telegram: "telegram-wallet",
  tonkeeper: "tonkeeper",
  mytonwallet: "mytonwallet",
} as const;

const APP_NAMES: Record<string, string> = { "telegram-wallet": "Wallet in Telegram", tonkeeper: "Tonkeeper", mytonwallet: "MyTonWallet" };
export const walletName = (appName: string | null | undefined) => (appName && APP_NAMES[appName.toLowerCase()]) || appName || "Your wallet";
export const shortAddress = (a: string) => `${a.slice(0, 4)}...${a.slice(-4)}`;

/** The bot's main Mini App, where a wallet app sends the player back after signing. */
const BOT_APP_URL = "https://t.me/Degensduelbot?startapp";

// TON Connect's UI is large, so it loads only when a player first opens a Money screen.
let ui: Promise<TonConnectUI> | null = null;
function tonConnect(): Promise<TonConnectUI> {
  ui ??= import("@tonconnect/ui").then(({ TonConnectUI }) => {
    const returnUrl = (import.meta.env.VITE_TWA_RETURN_URL ?? BOT_APP_URL) as `${string}://${string}`;
    return new TonConnectUI({
      manifestUrl: `${location.origin}/tonconnect-manifest.json`,
      // After signing, the wallet sends the player back into the Mini App.
      actionsConfiguration: { twaReturnUrl: returnUrl },
    });
  });
  return ui;
}

/** Thrown when the player closed the wallet list or turned the request down in their wallet. */
export class Cancelled extends Error {}

/**
 * Connects a wallet and proves it is the player's: the wallet signs our one-time payload, and the server checks
 * the signature before it accepts the address. Opens one wallet straight away when `app` is given, else the list.
 */
export async function connectWallet(app?: string): Promise<WalletView> {
  const view = await connectAndProve(app);
  cached = { ...view, fetchedAt: Date.now() };
  moneyChanged();
  return cached;
}

async function connectAndProve(app?: string): Promise<WalletView> {
  const t = await tonConnect();
  await t.connectionRestored;
  // A proof only comes with a new connection, so an old session is closed first.
  if (t.connected) await t.disconnect().catch(() => {});
  const { payload } = await api<{ payload: string }>("/v1/wallet/proof");
  t.setConnectRequestParameters({ state: "ready", value: { tonProof: payload } });
  const wallet = await new Promise<Wallet>((resolve, reject) => {
    let settled = false;
    const offStatus = t.onStatusChange(
      (w) => {
        if (!w || settled) return;
        settled = true;
        done();
        resolve(w);
      },
      (err) => {
        settled = true;
        done();
        reject(err);
      },
    );
    const offModal = t.onModalStateChange((s) => {
      if (s.status === "closed" && !settled && !t.connected) {
        settled = true;
        done();
        reject(new Cancelled());
      }
    });
    const done = () => {
      offStatus();
      offModal();
    };
    void (app ? t.openSingleWalletModal(app) : t.openModal()).catch((e) => {
      settled = true;
      done();
      reject(e);
    });
  });
  const reply = wallet.connectItems?.tonProof;
  if (!reply || !("proof" in reply)) {
    await t.disconnect().catch(() => {});
    throw new ApiError(400, "bad_proof");
  }
  try {
    return await api<WalletView>("/v1/wallet/connect", {
      method: "POST",
      body: { address: wallet.account.address, appName: wallet.device.appName, proof: { ...reply.proof, stateInit: wallet.account.walletStateInit } },
    });
  } catch (e) {
    await t.disconnect().catch(() => {});
    throw e;
  }
}

/**
 * Asks the connected wallet to send `amount` USDT to the vault. The wallet in this session must be the one the
 * player connected, or the USDT would come from somewhere the server does not expect: then it reconnects first.
 */
export async function depositFromWallet(amount: string, linked: string): Promise<void> {
  const t = await tonConnect();
  await t.connectionRestored;
  if (!t.wallet || !sameAddress(t.wallet.account.address, linked)) await connectWallet();
  const tx = await api<{ validUntil: number; messages: { address: string; amount: string; payload: string }[] }>("/v1/deposit/prepare", {
    method: "POST",
    body: { amount },
  });
  try {
    await t.sendTransaction(tx);
  } catch (e) {
    if ((e as Error).name === "UserRejectsError") throw new Cancelled();
    throw e;
  }
}

/** A sponsor paying their invoice from the connected wallet: what's still due, with its sp: comment. */
export async function payInvoiceFromWallet(dealId: string, linked: string): Promise<void> {
  const t = await tonConnect();
  await t.connectionRestored;
  if (!t.wallet || !sameAddress(t.wallet.account.address, linked)) await connectWallet();
  const tx = await api<{ validUntil: number; messages: { address: string; amount: string; payload: string }[] }>("/v1/sponsor/prepare", { method: "POST", body: { dealId } });
  try {
    await t.sendTransaction(tx);
  } catch (e) {
    if ((e as Error).name === "UserRejectsError") throw new Cancelled();
    throw e;
  }
}

function sameAddress(raw: string, friendly: string): boolean {
  // The server shows the friendly form; TON Connect reports the raw one. Both end in the same account hash.
  const hash = raw.split(":")[1];
  if (!hash) return false;
  const bytes = Uint8Array.from(atob(friendly.replace(/-/g, "+").replace(/_/g, "/")), (c) => c.charCodeAt(0));
  const hex = Array.from(bytes.slice(2, 34), (b) => b.toString(16).padStart(2, "0")).join("");
  return hex === hash.toLowerCase();
}

/** The Money screens' shared data: the connected wallet, deposit details and recent activity. Refreshes on deposits. */
export function useMoney(): { view: WalletView | null; error: string | null; reload: () => Promise<void> } {
  const [view, setView] = useState<WalletView | null>(cached);
  const [error, setError] = useState<string | null>(null);
  const reload = useCallback(async () => {
    try {
      cached = { ...(await api<WalletView>("/v1/wallet")), fetchedAt: Date.now() };
      setView(cached);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.code : "network");
    }
  }, []);
  useEffect(() => {
    void reload();
    const on = () => void reload();
    addEventListener("dd:money", on);
    return () => removeEventListener("dd:money", on);
  }, [reload]);
  return { view, error, reload };
}

let cached: WalletView | null = null;

/** Lets every Money screen know the balance moved (a deposit landed, a wallet connected). */
export function moneyChanged(detail?: Landed): void {
  if (detail) lastDeposit = detail;
  dispatchEvent(new CustomEvent("dd:money", { detail }));
}

/** A deposit the indexer just credited, as the live socket reports it, in units. */
export interface Landed {
  type: "deposit";
  credited: string;
  returned: string;
  at: number;
}

/** The last deposit that landed while the app was open, for the Deposit landed screen. */
export let lastDeposit: Landed | null = null;

/** Withdrawals stay shut until this time when the wallet was just switched; null when open. */
export function holdUntil(view: WalletView | null): number | null {
  if (!view?.wallet) return null;
  return view.wallet.withdrawAfter > serverNow(view) ? view.wallet.withdrawAfter : null;
}

/** The server's time now, so a countdown is right even when the phone's clock is off. */
export function serverNow(view: WalletView): number {
  return Date.now() + (view.serverTime - view.fetchedAt);
}

/** Turns "12.5" or "12,50" typed on a phone keypad into "12.50", or null if it is not an amount. */
export function cleanAmount(s: string): string | null {
  const v = s.trim().replace(",", ".");
  return /^\d{1,7}(\.\d{0,6})?$/.test(v) && Number(v) > 0 ? v.replace(/\.$/, "") : null;
}

/** A transaction on Tonviewer. Testnet addresses start with k or 0 in their friendly form. */
export function explorerTx(hash: string, view: WalletView | null): string {
  const testnet = !!view?.deposit.vault && /^[k0]/.test(view.deposit.vault);
  return `https://${testnet ? "testnet." : ""}tonviewer.com/transaction/${hash}`;
}

export interface Withdrawal {
  state: "paying" | "done" | "partial" | "failed";
  amount: string;
  received: string;
  fee: string;
  toWallet: string;
  createdAt: number;
  finishedAt: number | null;
  tx: string | null;
}

/** The withdrawal the player just asked for, followed until the vault's payout lands or comes back. */
export function useWithdrawal(id: string | null): Withdrawal | null {
  const [w, setW] = useState<Withdrawal | null>(null);
  useEffect(() => {
    if (!id) return;
    let stop = false;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const next = await api<Withdrawal>(`/v1/withdrawals/${encodeURIComponent(id)}`);
        if (stop) return;
        setW(next);
        if (next.state !== "paying") {
          moneyChanged();
          return;
        }
      } catch {
        /* a dropped connection: try again on the next tick */
      }
      if (!stop) timer = setTimeout(() => void poll(), 3000);
    };
    void poll();
    return () => {
      stop = true;
      clearTimeout(timer);
    };
  }, [id]);
  return w;
}

/** Where a player reaches support: the bot's support chat. Set per deployment. */
export const SUPPORT_URL: string = import.meta.env.VITE_SUPPORT_URL ?? "https://t.me/Degensduelbot";
